import { z } from 'zod';
import { hardwareAssetKindSchema, hardwareAssetStatusSchema, hardwareComponentFamilySchema, } from '../enums.js';
import { dataModalitySchema } from './objective.js';
/**
 * Flotte de matériels (ADR-0029) : le registre d'appareils sur lesquels les modèles sont entraînés
 * puis déployés. Le contrat décrit de la **donnée métier** — aucune notion de présentation, pour
 * qu'une refonte UX ultérieure (images, mise en page) ne le rouvre pas (ADR-0029 §5).
 *
 * Décision structurante : les deux familles de spécifications vivent dans **deux objets distincts**.
 * `compute` (calcul & capteur) est la seule enveloppe qui **adapte le modèle** ; `airframe` reste de
 * la **métadonnée d'inventaire**, jamais un paramètre du modèle. La frontière est ainsi impossible à
 * franchir par accident : une fonction de packaging edge reçoit `asset.compute` et n'a
 * structurellement pas accès à l'airframe.
 */
const shortText = z.string().trim().max(120);
const optionalShortText = shortText.optional().or(z.literal('').transform(() => undefined));
/**
 * Enveloppe **calcul & capteur** — ce qui adapte le modèle (ADR-0014 phase D, ADR-0029 §2). Tous les
 * champs sont facultatifs : un matériel se saisit progressivement. La modalité réutilise le
 * référentiel de l'objectif (contrat unique, jamais dupliqué).
 */
export const hardwareComputeSchema = z.object({
    soc: optionalShortText, // SoC / hôte, ex. « Jetson Orin NX »
    accelerator: optionalShortText, // accélérateur, ex. « Ampere 1024 c. »
    tops: z.number().nonnegative().max(100_000).optional(), // budget de calcul (TOPS)
    latencyMs: z.number().int().nonnegative().max(600_000).optional(), // latence cible
    modality: dataModalitySchema.optional(),
    sensorFeed: optionalShortText, // flux capteur, ex. « 4K · 30 ips »
    inputResolution: optionalShortText, // résolution d'entrée, ex. « 3840×2160 »
    fovDeg: z.number().nonnegative().max(360).optional(), // champ de vue (degrés)
});
/**
 * Métadonnées **airframe** (ADR-0029 §2). Inventaire et planification terrain. **N'entrent jamais**
 * comme paramètre du modèle — d'où leur objet séparé.
 */
export const hardwareAirframeSchema = z.object({
    massG: z.number().int().nonnegative().max(10_000_000).optional(), // masse (g)
    enduranceMin: z.number().int().nonnegative().max(100_000).optional(), // endurance (min)
    propulsion: optionalShortText, // motorisation
    maxSpeedMs: z.number().nonnegative().max(10_000).optional(), // vitesse max (m/s)
    registration: optionalShortText, // immatriculation
    homeBase: optionalShortText, // base / stockage
});
/** Composant rattaché à un matériel, étiqueté par famille (traçabilité + séparation, ADR-0029 §3). */
export const hardwareComponentInputSchema = z.object({
    family: hardwareComponentFamilySchema,
    name: shortText.min(1),
    detail: optionalShortText,
});
export const hardwareComponentSchema = hardwareComponentInputSchema.extend({
    id: z.string(),
});
/**
 * Corps de création / mise à jour d'un matériel. Les composants voyagent avec le matériel : l'écriture
 * **remplace** la liste entière (idempotent), on ne gère pas d'endpoints de composants séparés pour
 * l'incrément 1 (ADR-0029 §4).
 */
export const upsertHardwareAssetSchema = z.object({
    kind: hardwareAssetKindSchema,
    name: shortText.min(1),
    reference: optionalShortText,
    status: hardwareAssetStatusSchema,
    compute: hardwareComputeSchema.default({}),
    airframe: hardwareAirframeSchema.default({}),
    components: z.array(hardwareComponentInputSchema).max(50).default([]),
});
/** Matériel tel que restitué par l'API. */
export const hardwareAssetSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    kind: hardwareAssetKindSchema,
    name: z.string(),
    reference: z.string().nullable(),
    status: hardwareAssetStatusSchema,
    compute: hardwareComputeSchema,
    airframe: hardwareAirframeSchema,
    components: z.array(hardwareComponentSchema),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/* ------------------------------------------------------------------ *
 * Photos d'un build (ADR-0032)
 * ------------------------------------------------------------------ */
/**
 * Photos d'un build de matériel (ADR-0032). Déposées **depuis le navigateur** via URL pré-signée —
 * l'octet ne traverse pas l'API (CLAUDE.md §2). Cloisonnées, rattachées à un `HardwareAsset`.
 */
export const HARDWARE_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Taille maximale d'une photo de build (8 Mo), vérifiée à la confirmation côté serveur (ADR-0032). */
export const HARDWARE_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
/** Ouverture d'un dépôt de photo : le type est en liste blanche (images seulement). */
export const reserveHardwarePhotoSchema = z.object({
    contentType: z.enum(HARDWARE_PHOTO_CONTENT_TYPES),
    caption: z.string().trim().max(200).optional(),
});
/** Réponse d'ouverture : URL de dépôt pré-signée, valable un temps borné. */
export const hardwarePhotoUploadSchema = z.object({
    id: z.string(),
    uploadUrl: z.string().url(),
    expiresAt: z.coerce.date(),
});
/** Photo telle que restituée : `url` est une URL de lecture pré-signée (courte durée). */
export const hardwarePhotoSchema = z.object({
    id: z.string(),
    url: z.string().url(),
    caption: z.string().nullable(),
    contentType: z.string(),
    createdAt: z.coerce.date(),
});
//# sourceMappingURL=hardware.js.map